import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Upload,
  Trash2,
  Eye,
  Tag,
  UserPlus,
  Pencil,
  AlignLeft,
  FileType,
  CornerDownRight,
  Paperclip,
  ChevronDown,
} from "lucide-react";
import { useNotification } from "../contexts/NotificationContext";
import { useProject } from "../contexts/ProjectContext";
import { useUser } from "../contexts/UserContext";
import { cardAPI, columnAPI, projectAPI } from "../utils/api";
import ConfirmationModal from "./ConfirmationModal";
import SimpleQuillEditor from "./SimpleQuillEditor";
import CustomStatusDropdown from "./CustomStatusDropdown";
import Avatar from "./Avatar";

const labelColors = [
  { name: "Light Green", value: "light-green", bg: "bg-green-300", text: "text-black" },
  { name: "Green", value: "green", bg: "bg-green-500", text: "text-white" },
  { name: "Dark Green", value: "dark-green", bg: "bg-green-700", text: "text-white" },
  { name: "Light Yellow", value: "light-yellow", bg: "bg-yellow-300", text: "text-black" },
  { name: "Yellow", value: "yellow", bg: "bg-yellow-500", text: "text-black" },
  { name: "Dark Yellow", value: "dark-yellow", bg: "bg-yellow-700", text: "text-white" },
  { name: "Orange", value: "orange", bg: "bg-orange-500", text: "text-white" },
  { name: "Red", value: "red", bg: "bg-red-500", text: "text-white" },
  { name: "Purple", value: "purple", bg: "bg-purple-500", text: "text-white" },
  { name: "Pink", value: "pink", bg: "bg-pink-500", text: "text-white" },
  { name: "Blue", value: "blue", bg: "bg-blue-500", text: "text-white" },
  { name: "Gray", value: "gray", bg: "bg-gray-500", text: "text-white" },
];

const getLabelColor = (color) => {
  if (color === "light-green") return labelColors.find((c) => c.value === "green");
  if (color === "light-yellow") return labelColors.find((c) => c.value === "yellow");
  return labelColors.find((c) => c.value === (color || "blue")) || labelColors[10];
};

const CreateLabelsDropdown = ({
  isOpen,
  onClose,
  anchorRef,
  availableLabels,
  selectedLabels,
  searchTerm,
  setSearchTerm,
  showCreateLabel,
  setShowCreateLabel,
  newLabelName,
  setNewLabelName,
  newLabelColor,
  setNewLabelColor,
  onToggleLabel,
  onCreateLabel,
  onUpdateLabel,
}) => {
  const dropdownRef = useRef(null);
  const [colorblindFriendly, setColorblindFriendly] = useState(false);
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [editLabelName, setEditLabelName] = useState("");
  const [editLabelColor, setEditLabelColor] = useState("blue");
  const [savingLabelId, setSavingLabelId] = useState(null);
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if (isOpen && anchorRef?.current) {
      const updatePosition = () => {
        const rect = anchorRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUp = spaceBelow < 280 && rect.top > 280;
        setCoords({
          top: openUp ? rect.top - 4 : rect.bottom + 4,
          left: rect.left,
          width: Math.max(rect.width, 280),
          openUp,
        });
      };
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }
  }, [isOpen, anchorRef, showCreateLabel]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        (!anchorRef?.current || !anchorRef.current.contains(event.target))
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  useEffect(() => {
    if (!isOpen) {
      setShowCreateLabel(false);
      setEditingLabelId(null);
      setEditLabelName("");
      setEditLabelColor("blue");
      setSavingLabelId(null);
    }
  }, [isOpen, setShowCreateLabel]);

  if (!isOpen || !coords) return null;

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredLabels = availableLabels.filter((label) =>
    (label.name || "").toLowerCase().includes(normalizedSearch)
  );

  const handleCreateClick = () => {
    onCreateLabel();
  };

  const startEditingLabel = (label) => {
    const labelId = label._id || label.id || label.name;
    setEditingLabelId(labelId);
    setEditLabelName(label.name || "");
    setEditLabelColor(label.color || "blue");
  };

  const cancelEditingLabel = () => {
    setEditingLabelId(null);
    setEditLabelName("");
    setEditLabelColor("blue");
    setSavingLabelId(null);
  };

  const saveEditingLabel = async (label) => {
    const nextName = editLabelName.trim();
    if (!nextName) return;

    const labelId = label._id || label.id || label.name;
    setSavingLabelId(labelId);
    const updated = await onUpdateLabel(label, {
      name: nextName,
      color: editLabelColor,
    });
    setSavingLabelId(null);

    if (updated) {
      cancelEditingLabel();
    }
  };

  const renderLabelRow = (label) => {
    const colorConfig = getLabelColor(label.color);
    const isSelected = selectedLabels.some((item) => item.name === label.name);
    const labelKey = label._id || label.id || label.name;
    const colorName = colorConfig?.name || label.color || "Label";

    const isEditing = editingLabelId === labelKey;
    const isSaving = savingLabelId === labelKey;

    if (isEditing) {
      return (
        <div key={labelKey} className="p-2 bg-gray-50 dark:bg-slate-700/50 rounded-md my-1 space-y-2 border border-gray-200 dark:border-slate-600">
          <input
            type="text"
            value={editLabelName}
            onChange={(e) => setEditLabelName(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === "Enter") saveEditingLabel(label);
              if (e.key === "Escape") cancelEditingLabel();
            }}
            className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            autoFocus
            aria-label="Label name"
          />
          <select
            value={editLabelColor}
            onChange={(e) => setEditLabelColor(e.target.value)}
            className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            aria-label="Label color"
          >
            {labelColors.map((color) => (
              <option key={color.value} value={color.value}>
                {color.name}
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => saveEditingLabel(label)}
              disabled={isSaving || !editLabelName.trim()}
              className="flex-1 rounded bg-blue-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={cancelEditingLabel}
              disabled={isSaving}
              className="flex-1 rounded border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    return (
      <div key={labelKey} className="flex items-center gap-2 py-0.5">
        <label className="flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => {
              onToggleLabel(label);
              setSearchTerm?.("");
            }}
            className="h-4 w-4 rounded border-gray-400 bg-transparent text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-slate-500 dark:bg-slate-900"
            aria-label={`${isSelected ? "Remove" : "Apply"} ${label.name}`}
          />
        </label>
        <button
          type="button"
          onClick={() => {
            onToggleLabel(label);
            setSearchTerm?.("");
          }}
          className={`flex min-h-[32px] flex-1 items-center rounded px-3 text-left text-xs font-semibold transition-opacity hover:opacity-90 ${
            colorConfig?.bg || "bg-blue-500"
          } ${colorConfig?.text || "text-white"}`}
        >
          <span className="truncate">
            {colorblindFriendly && <span className="mr-2 opacity-80 font-normal">[{colorName}]</span>}
            {label.name}
          </span>
        </button>
        <button
          type="button"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white"
          title="Edit label"
          aria-label={`Edit ${label.name}`}
          onClick={() => startEditingLabel(label)}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };

  return createPortal(
    <div
      ref={dropdownRef}
      style={{
        position: "fixed",
        top: coords.openUp ? undefined : `${coords.top}px`,
        bottom: coords.openUp ? `${window.innerHeight - coords.top}px` : undefined,
        left: `${coords.left}px`,
        width: `${coords.width}px`,
        zIndex: 99999,
      }}
      className="portal-dropdown bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg shadow-2xl overflow-hidden animate-slideDownFade"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="relative flex items-center justify-center px-8 py-2.5 border-b border-gray-100 dark:border-slate-700">
        <h3 className="text-xs font-semibold text-gray-700 dark:text-slate-300">Labels</h3>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-700 dark:text-slate-400 dark:hover:text-white transition-colors"
          title="Close labels"
          aria-label="Close labels"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="p-3">
        {/* Labels List */}
        <div className="mb-2">
          <h4 className="px-0.5 pb-1 text-[11px] font-semibold text-gray-500 dark:text-slate-400">Labels</h4>
          <div className={`${showCreateLabel ? "max-h-[76px]" : "max-h-[160px]"} overflow-y-auto space-y-1 pr-0.5 scrollbar-thin`}>
            {filteredLabels.length > 0 ? (
              filteredLabels.map(renderLabelRow)
            ) : (
              <p className="px-2 py-3 text-center text-xs text-gray-500 dark:text-slate-400">
                No labels found
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-2 pt-1 border-t border-gray-100 dark:border-slate-700">
          {showCreateLabel ? (
            <div className="space-y-2 rounded border border-gray-200 dark:border-slate-700 p-2 bg-gray-50/50 dark:bg-slate-900/40">
              <input
                type="text"
                value={newLabelName}
                onChange={(e) => setNewLabelName(e.target.value.toUpperCase())}
                placeholder="Label name"
                className="w-full px-2.5 py-1.5 text-xs bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded text-gray-900 dark:text-slate-100 outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
              <select
                value={newLabelColor}
                onChange={(e) => setNewLabelColor(e.target.value)}
                className="w-full px-2 py-1.5 text-xs bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded text-gray-900 dark:text-slate-100 outline-none focus:ring-1 focus:ring-blue-500"
              >
                {labelColors.map((color) => (
                  <option key={color.value} value={color.value}>
                    {color.name}
                  </option>
                ))}
              </select>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleCreateClick}
                  disabled={!newLabelName.trim()}
                  className="flex-1 py-1.5 px-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50"
                >
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateLabel(false)}
                  className="flex-1 py-1.5 px-2 text-xs font-semibold text-gray-600 dark:text-slate-300 border border-gray-300 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowCreateLabel(true)}
              className="w-full rounded border border-gray-300 dark:border-slate-600 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              Create a new label
            </button>
          )}

          <button
            type="button"
            onClick={() => setColorblindFriendly((prev) => !prev)}
            className={`w-full rounded border px-3 py-1.5 text-xs font-semibold transition-colors ${
              colorblindFriendly
                ? "border-blue-500 bg-blue-600 text-white hover:bg-blue-700"
                : "border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700"
            }`}
            aria-pressed={colorblindFriendly}
          >
            {colorblindFriendly ? "Disable colorblind friendly mode" : "Enable colorblind friendly mode"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const CreateMembersDropdown = ({
  isOpen,
  onClose,
  anchorRef,
  memberSearch,
  projectMembers,
  selectedAssignees,
  onToggleAssignee,
}) => {
  const dropdownRef = useRef(null);
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if (isOpen && anchorRef?.current) {
      const updatePosition = () => {
        const rect = anchorRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUp = spaceBelow < 280 && rect.top > 280;
        setCoords({
          top: openUp ? rect.top - 4 : rect.bottom + 4,
          left: rect.left,
          width: Math.max(rect.width, 280),
          openUp,
        });
      };
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }
  }, [isOpen, anchorRef]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        (!anchorRef?.current || !anchorRef.current.contains(event.target))
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen || !coords) return null;

  const normalizedSearch = (memberSearch || "").trim().toLowerCase();
  const matchesSearch = (member) => {
    if (!normalizedSearch) return true;
    return [member.name, member.email, member.role]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalizedSearch));
  };

  const assignedMembers = projectMembers
    .filter((member) => selectedAssignees.includes(member._id))
    .filter(matchesSearch);
  const boardMembers = projectMembers
    .filter((member) => !selectedAssignees.includes(member._id))
    .filter(matchesSearch);

  const renderMemberRow = (member, isAssigned) => (
    <button
      key={member._id}
      type="button"
      onClick={() => onToggleAssignee(member)}
      className={`group flex w-full items-center justify-between gap-2.5 px-2.5 py-1.5 text-left transition-colors duration-150 rounded ${
        isAssigned
          ? "bg-blue-50/80 text-blue-900 hover:bg-blue-100/80 dark:bg-blue-950/40 dark:text-blue-200 dark:hover:bg-blue-900/50"
          : "text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700/60"
      }`}
      aria-label={`${isAssigned ? "Remove" : "Assign"} ${member.name || "member"}`}
    >
      <span className="flex min-w-0 items-center gap-2">
        <Avatar user={member} size="xs" />
        <span className="min-w-0">
          <span className="block truncate text-xs font-medium leading-4">{member.name || "Unknown User"}</span>
          {member.email && (
            <span className="block truncate text-[10px] leading-3 text-gray-500 dark:text-slate-400">{member.email}</span>
          )}
        </span>
      </span>
      {isAssigned && (
        <X className="h-3.5 w-3.5 shrink-0 text-gray-400 group-hover:text-red-500 dark:text-slate-400 transition-colors" />
      )}
    </button>
  );

  return createPortal(
    <div
      ref={dropdownRef}
      style={{
        position: "fixed",
        top: coords.openUp ? undefined : `${coords.top}px`,
        bottom: coords.openUp ? `${window.innerHeight - coords.top}px` : undefined,
        left: `${coords.left}px`,
        width: `${coords.width}px`,
        zIndex: 99999,
      }}
      className="portal-dropdown bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg shadow-2xl overflow-hidden animate-slideDownFade"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="relative flex items-center justify-center px-8 py-2.5 border-b border-gray-100 dark:border-slate-700">
        <h3 className="text-xs font-semibold text-gray-700 dark:text-slate-300">Members</h3>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-700 dark:text-slate-400 dark:hover:text-white transition-colors"
          title="Close members"
          aria-label="Close members"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="p-3">
        <div className="max-h-[220px] overflow-y-auto space-y-2 pr-0.5 scrollbar-thin">
          <section aria-labelledby="card-members-heading">
            <h4 id="card-members-heading" className="px-0.5 pb-1 text-[11px] font-semibold text-gray-500 dark:text-slate-400">Card members</h4>
            <div className="space-y-0.5">
              {assignedMembers.length > 0 ? (
                assignedMembers.map((member) => renderMemberRow(member, true))
              ) : (
                <p className="px-2 py-1 text-xs text-gray-500 dark:text-slate-400 italic">No card members</p>
              )}
            </div>
          </section>

          <section aria-labelledby="board-members-heading" className="pt-2 border-t border-gray-100 dark:border-slate-700">
            <h4 id="board-members-heading" className="px-0.5 pb-1 text-[11px] font-semibold text-gray-500 dark:text-slate-400">Board members</h4>
            <div className="space-y-0.5">
              {boardMembers.length > 0 ? (
                boardMembers.map((member) => renderMemberRow(member, false))
              ) : (
                <p className="px-2 py-1 text-xs text-gray-500 dark:text-slate-400 italic">
                  {projectMembers.length === 0 ? "No project members found" : "No board members found"}
                </p>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
};
const CreateCardModal = ({ projectId, onClose, onCardCreated, defaultStatus = "todo" }) => {
  const [formData, setFormData] = useState({ title: "", description: "", status: defaultStatus, dueDate: "" });
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const [loading, setLoading] = useState(false);
  const [projectData, setProjectData] = useState(null);
  const [availableLabels, setAvailableLabels] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [statusOptions, setStatusOptions] = useState([{ value: defaultStatus, label: defaultStatus, color: "blue" }]);
  const [loadingProjectData, setLoadingProjectData] = useState(false);
  const [showLabelsDropdown, setShowLabelsDropdown] = useState(false);
  const [showMembersDropdown, setShowMembersDropdown] = useState(false);
  const labelsSectionRef = useRef(null);
  const assigneeSectionRef = useRef(null);
  const labelInputRef = useRef(null);
  const memberInputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [showCreateLabel, setShowCreateLabel] = useState(false);
  const [newLabelName, setNewLabelName] = useState("");
  const [newLabelColor, setNewLabelColor] = useState("blue");
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, index: null });
  const [viewingImage, setViewingImage] = useState(null);

  const { currentProject } = useProject();
  const { user } = useUser();
  const { showToast } = useNotification();
  const modalRef = useRef(null);
  const fileInputRef = useRef(null);
  const attachmentDropzoneRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        if (event.target.closest?.(".portal-dropdown")) return;
        if (!viewingImage && !deleteConfirm.show && !showLabelsDropdown && !showMembersDropdown) {
          onClose();
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [deleteConfirm.show, onClose, showLabelsDropdown, showMembersDropdown, viewingImage]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && viewingImage) setViewingImage(null);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [viewingImage]);

  useEffect(() => {
    return () => {
      previews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [previews]);

  useEffect(() => {
    let cancelled = false;
    const loadProjectData = async () => {
      if (!projectId) return;
      setLoadingProjectData(true);
      try {
        const [projectResponse, columnsResponse, labelsResponse] = await Promise.all([
          projectAPI.getProject(projectId),
          columnAPI.getColumns(projectId),
          projectAPI.getProjectLabels(projectId),
        ]);
        if (cancelled) return;
        const project = projectResponse.data?.project || null;
        const columns = columnsResponse.data?.columns || [];
        const labels = labelsResponse.data?.labels || [];
        setProjectData(project);
        setAvailableLabels(labels);
        const options = columns.filter((column) => column.status !== "archive").map((column) => ({ value: column.status, label: column.name, color: column.color || "blue" }));
        const dedupedOptions = options.filter((option, index, arr) => arr.findIndex((item) => item.value === option.value) === index);
        if (dedupedOptions.length > 0) {
          setStatusOptions(dedupedOptions);
          if (!dedupedOptions.find((option) => option.value === formData.status)) {
            setFormData((prev) => ({ ...prev, status: dedupedOptions[0].value }));
          }
        }
      } catch (error) {
        console.error("Error loading create card data:", error);
        showToast("Failed to load project metadata", "error");
      } finally {
        if (!cancelled) setLoadingProjectData(false);
      }
    };
    loadProjectData();
    return () => {
      cancelled = true;
    };
  }, [formData.status, projectId, showToast]);

  const projectMembers = useMemo(() => {
    const members = projectData?.members || currentProject?.members || [];
    return members
      .map((member) => {
        if (!member) return null;
        const resolvedUser = member.user || member;
        return { ...resolvedUser, role: member.role || resolvedUser.role };
      })
      .filter(Boolean);
  }, [currentProject?.members, projectData?.members]);

  const selectedMembers = useMemo(() => {
    const list = projectMembers.filter((member) =>
      selectedAssignees.includes(member._id || member.id)
    );
    if (
      user &&
      selectedAssignees.includes(user._id || user.id) &&
      !list.some((m) => (m._id || m.id) === (user._id || user.id))
    ) {
      list.push(user);
    }
    return list;
  }, [projectMembers, selectedAssignees, user]);

  const currentUserId = user?._id || user?.id;
  const isCurrentUserAssigned = Boolean(
    currentUserId && selectedAssignees.includes(currentUserId)
  );
  const assigneeButtonLabel = isCurrentUserAssigned ? "Change" : "Assign to me";

  const handleToggleLabel = (label) => {
    setSearchTerm("");
    setSelectedLabels((prev) => {
      const exists = prev.some((item) => item.name === label.name);
      if (exists) return prev.filter((item) => item.name !== label.name);
      return [...prev, { name: label.name, color: label.color || "blue" }];
    });
  };

  const handleCreateLabel = async () => {
    if (!newLabelName.trim() || !projectId) return;
    try {
      const response = await projectAPI.createLabel(projectId, { name: newLabelName.trim(), color: newLabelColor });
      if (response.data.success) {
        const createdLabel = response.data.label || { name: newLabelName.trim(), color: newLabelColor };
        setAvailableLabels((prev) => (prev.some((label) => label.name === createdLabel.name) ? prev : [...prev, createdLabel]));
        setSelectedLabels((prev) => (prev.some((label) => label.name === createdLabel.name) ? prev : [...prev, createdLabel]));
        setNewLabelName("");
        setNewLabelColor("blue");
        setShowCreateLabel(false);
        setSearchTerm("");
        showToast("Label created and applied successfully!", "success");
      }
    } catch (error) {
      console.error("Error creating label:", error);
      showToast("Failed to create label", "error");
    }
  };

  const handleUpdateLabel = async (label, updates) => {
    const labelId = label._id || label.id;
    if (!projectId || !labelId) {
      showToast("Cannot edit this label because its project label ID is missing", "error");
      return false;
    }

    const nextLabel = {
      name: updates.name.trim(),
      color: updates.color || label.color || "blue",
    };

    const nameChanged = nextLabel.name !== label.name;
    const colorChanged = nextLabel.color !== label.color;
    if (!nameChanged && !colorChanged) {
      showToast("No changes to save", "info");
      return true;
    }

    try {
      const response = await projectAPI.updateLabel(projectId, labelId, nextLabel);
      if (!response.data.success) {
        showToast("Failed to update label", "error");
        return false;
      }

      const updatedLabel = response.data.label || { ...label, ...nextLabel, _id: labelId };
      setAvailableLabels((prev) =>
        prev.map((item) => ((item._id || item.id) === labelId ? { ...item, ...updatedLabel } : item))
      );
      setSelectedLabels((prev) =>
        prev.map((item) =>
          item.name === label.name
            ? { name: updatedLabel.name, color: updatedLabel.color || "blue" }
            : item
        )
      );
      showToast("Label updated successfully!", "success");
      return true;
    } catch (error) {
      console.error("Error updating label:", error);
      showToast(error.response?.data?.message || "Failed to update label", "error");
      return false;
    }
  };

  const handleToggleAssignee = (member) => {
    const memberId = member._id || member.id;
    if (!memberId) return;
    setMemberSearch("");
    // Single-assignee model: swap out any existing assignee for the new one, just like CardModal.js
    setSelectedAssignees((prev) =>
      prev.includes(memberId)
        ? []
        : [memberId]
    );
  };

  const handleFileChange = (e) => {
    processFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const processFiles = (selectedFiles) => {
    const validFileTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/csv",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ];
    const allowedFiles = selectedFiles.filter((file) => validFileTypes.includes(file.type));
    if (allowedFiles.length < selectedFiles.length) {
      showToast("Supported files: JPG, PNG, WEBP, XLSX, XLS, CSV, DOCX, PDF, PPTX", "warning");
    }
    if (allowedFiles.length === 0) return;
    const newPreviews = allowedFiles.map((file) => ({
      id: Math.random().toString(36).substring(7),
      url: URL.createObjectURL(file),
      name: file.name,
      type: file.type,
      isImage: file.type.startsWith("image/"),
      extension: file.name.split(".").pop()?.toUpperCase() || "FILE",
    }));
    setFiles((prev) => [...prev, ...allowedFiles]);
    setPreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeFile = (index) => setDeleteConfirm({ show: true, index });

  const handleConfirmDelete = () => {
    const { index } = deleteConfirm;
    if (typeof index !== "number") return;
    if (previews[index]) URL.revokeObjectURL(previews[index].url);
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
    setDeleteConfirm({ show: false, index: null });
    showToast("Image removed", "info");
  };

  const handleUploadAreaClick = () => {
    attachmentDropzoneRef.current?.focus();
    fileInputRef.current?.click();
  };

  const handleUploadAreaKeyDown = (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    handleUploadAreaClick();
  };

  const hasDraggedFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");

  const handleDragEnter = (e) => {
    e.preventDefault();
    if (!hasDraggedFiles(e)) return;
    dragCounter.current += 1;
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!hasDraggedFiles(e)) return;
    e.dataTransfer.dropEffect = "copy";
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (!hasDraggedFiles(e)) return;
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    processFiles(Array.from(e.dataTransfer.files || []));
  };

  const handlePaste = (e) => {
    if (e.target?.closest?.("textarea, input:not([type='file']), [contenteditable='true']")) {
      return;
    }
    const clipboardFiles = Array.from(e.clipboardData?.files || []);
    if (clipboardFiles.length === 0) return;
    e.preventDefault();
    processFiles(clipboardFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      showToast("Card title is required", "error");
      return;
    }
    setLoading(true);
    try {
      const cardData = {
        title: formData.title,
        description: formData.description,
        project: projectId,
        status: formData.status,
        dueDate: formData.dueDate || undefined,
        assignees: selectedAssignees,
        labels: selectedLabels,
      };
      const response = await cardAPI.createCard(cardData);
      let newCard = response.data.card;
      if (files.length > 0) {
        const formDataUpload = new FormData();
        files.forEach((file) => formDataUpload.append("files", file));
        try {
          const uploadResponse = await cardAPI.uploadFiles(newCard._id, formDataUpload);
          newCard = uploadResponse.data.card;
        } catch (uploadError) {
          console.error("Error uploading files:", uploadError);
          showToast("Card created, but image upload failed", "warning");
        }
      }
      onCardCreated(newCard);
      onClose();
    } catch (error) {
      console.error("Error creating card:", error);
      console.error("Error response:", error.response?.data);
      showToast("Failed to create card", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  return (
    <>
      <div className="modal-overlay">
        <div
          ref={modalRef}
          onPaste={handlePaste}
          className="bg-[#FEFCFF] dark:bg-slate-800 rounded-lg shadow-2xl
               w-full max-w-4xl max-h-[90vh]
               flex flex-col overflow-hidden my-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 h-[61.19px] min-h-[61.19px] max-h-[61.19px] bg-[#F1F2F4] dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 shrink-0 box-border">
            <h2 className="text-[20px] font-semibold tracking-tight">Create New Card</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] flex items-center justify-center rounded-lg hover:bg-gray-400/20 text-gray-600 dark:text-slate-400"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable Body */}
          <form
            onSubmit={handleSubmit}
            className="flex flex-col flex-1 min-h-0 overflow-hidden"
          >
            <div className="flex-1 overflow-y-auto p-6 md:p-7 space-y-4 md:space-y-5">
              {/* ALL YOUR EXISTING FORM FIELDS HERE */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-3 status-date rounded-lg transition-colors duration-200 group">
                  <div>
                    <label className="flex item-center gap-2 text-sm font-semibold text-secondary-700 dark:text-slate-300 mb-2">
                      <CornerDownRight className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                      Status
                    </label>
                    <CustomStatusDropdown value={formData.status} options={statusOptions} onChange={(value) => setFormData((prev) => ({ ...prev, status: value }))} isLoading={loadingProjectData} />
                  </div>
                </div>
                <div className="md:col-span-9">
                  <label htmlFor="title" className="flex item-center gap-2 text-sm font-semibold text-secondary-700 dark:text-slate-300 mb-2">
                    <FileType className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                    Card Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full h-8 px-3 text-xs border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-black dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                    placeholder="Enter card title"
                    required
                  />
                </div>
              </div>
              <div className=" rounded-lg transition-colors duration-200 group">
                <div className="flex items-center justify-between mb-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-slate-300"><AlignLeft className="w-4 h-4 text-gray-600 dark:text-slate-400" />Description</label>
                </div>
                <div className="simple-quill-editor description-editor">
                  <SimpleQuillEditor value={formData.description || ""} onChange={(content) => setFormData((prev) => ({ ...prev, description: content }))} placeholder="Add a description..." height={120} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <div ref={labelsSectionRef} className="relative">
                  {/* Label */}
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                    <Tag className="w-4 h-4" />
                    Labels
                  </label>

                  {/* Direct Searchable Input Trigger */}
                  <div
                    onClick={() => {
                      setShowLabelsDropdown(true);
                      labelInputRef.current?.focus();
                    }}
                    className={`w-full min-h-[32px] py-1 px-2.5 border rounded-lg bg-white dark:bg-slate-900 flex items-center justify-between gap-1.5 transition-all text-xs cursor-text ${
                      showLabelsDropdown
                        ? "border-blue-500 ring-1 ring-blue-500"
                        : "border-gray-300 dark:border-slate-600 hover:border-blue-400"
                    }`}
                  >
                    <div className="flex flex-wrap items-center gap-1.5 min-w-0 flex-1">
                      {selectedLabels.map((label) => {
                        const colorConfig = getLabelColor(label.color);

                        return (
                          <span
                            key={label.name}
                            className={`inline-flex items-center gap-1 px-2 h-[22px] rounded text-xs font-medium shadow-xs ${colorConfig?.bg || "bg-blue-500"} ${colorConfig?.text || "text-white"}`}
                          >
                            <span className="truncate max-w-[140px] leading-tight">{label.name}</span>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedLabels((prev) =>
                                  prev.filter((item) => item.name !== label.name)
                                );
                              }}
                              className="hover:opacity-80 transition-opacity focus:outline-none flex-shrink-0"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        );
                      })}
                      <input
                        ref={labelInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          if (!showLabelsDropdown) setShowLabelsDropdown(true);
                        }}
                        onFocus={() => setShowLabelsDropdown(true)}
                        placeholder={selectedLabels.length === 0 ? "Search labels..." : ""}
                        className="bg-transparent outline-none flex-1 min-w-[60px] text-xs text-gray-900 dark:text-slate-100 placeholder:text-gray-400"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowLabelsDropdown((prev) => !prev);
                      }}
                      className="p-0.5 text-gray-500 hover:text-gray-700 dark:hover:text-slate-300 flex-shrink-0 self-center"
                    >
                      <ChevronDown
                        className={`w-4 h-4 transition-transform flex-shrink-0 ${
                          showLabelsDropdown ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </div>
                  <CreateLabelsDropdown
                    isOpen={showLabelsDropdown}
                    onClose={() => setShowLabelsDropdown(false)}
                    anchorRef={labelsSectionRef}
                    availableLabels={availableLabels}
                    selectedLabels={selectedLabels}
                    searchTerm={searchTerm}
                    showCreateLabel={showCreateLabel}
                    setShowCreateLabel={setShowCreateLabel}
                    newLabelName={newLabelName}
                    setNewLabelName={setNewLabelName}
                    newLabelColor={newLabelColor}
                    setNewLabelColor={setNewLabelColor}
                    onToggleLabel={handleToggleLabel}
                    onCreateLabel={handleCreateLabel}
                    onUpdateLabel={handleUpdateLabel}
                  />
                </div>

                <div ref={assigneeSectionRef} className="assignees relative">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <UserPlus className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                        <label className="text-sm font-semibold text-gray-700 dark:text-slate-300">
                          Assignee ({selectedMembers.length})
                        </label>
                      </div>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => {
                          if (isCurrentUserAssigned) {
                            setShowMembersDropdown((prev) => !prev);
                          } else if (user) {
                            handleToggleAssignee(user);
                          } else {
                            setShowMembersDropdown(true);
                          }
                        }}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-xs font-semibold cursor-pointer"
                      >
                        {assigneeButtonLabel}
                      </button>
                    </div>

                    {/* Direct Searchable Input Trigger */}
                    <div
                      onClick={() => {
                        setShowMembersDropdown(true);
                        memberInputRef.current?.focus();
                      }}
                      className={`w-full h-8 min-h-[32px] px-2.5 border rounded-lg bg-white dark:bg-slate-900 flex items-center justify-between gap-1.5 transition-all text-xs cursor-text ${
                        showMembersDropdown
                          ? "border-blue-500 ring-1 ring-blue-500"
                          : "border-gray-300 dark:border-slate-600 hover:border-blue-400"
                      }`}
                    >
                      <div className="flex flex-wrap items-center gap-1.5 min-w-0 flex-1">
                        {selectedMembers.map((member) => (
                          <span
                            key={member._id}
                            className="inline-flex items-center gap-1.5 pl-0.5 pr-1.5 h-[22px] rounded text-xs font-medium bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-slate-200 border border-gray-200 dark:border-slate-700 shadow-xs"
                          >
                            <Avatar user={member} size="xxs" />
                            <span className="truncate max-w-[90px] leading-none">{member.name || "User"}</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleAssignee(member);
                              }}
                              className="hover:opacity-80 transition-opacity focus:outline-none flex-shrink-0"
                            >
                              <X className="w-3 h-3 text-gray-400 hover:text-red-500" />
                            </button>
                          </span>
                        ))}
                        <input
                          ref={memberInputRef}
                          type="text"
                          value={memberSearch}
                          onChange={(e) => {
                            setMemberSearch(e.target.value);
                            if (!showMembersDropdown) setShowMembersDropdown(true);
                          }}
                          onFocus={() => setShowMembersDropdown(true)}
                          placeholder={selectedMembers.length === 0 ? "Search assignees..." : ""}
                          className="bg-transparent outline-none flex-1 min-w-[60px] text-xs text-gray-900 dark:text-slate-100 placeholder:text-gray-400"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMembersDropdown((prev) => !prev);
                        }}
                        className="p-0.5 text-gray-500 hover:text-gray-700 dark:hover:text-slate-300 flex-shrink-0 self-center"
                      >
                        <ChevronDown
                          className={`w-4 h-4 transition-transform flex-shrink-0 ${
                            showMembersDropdown ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  <CreateMembersDropdown
                    isOpen={showMembersDropdown}
                    onClose={() => setShowMembersDropdown(false)}
                    anchorRef={assigneeSectionRef}
                    memberSearch={memberSearch}
                    projectMembers={projectMembers}
                    selectedAssignees={selectedAssignees}
                    onToggleAssignee={handleToggleAssignee}
                  />
                </div>
              </div>
              <div>
                <label className="flex item-center gap-2 text-sm font-semibold text-secondary-700 dark:text-slate-300 mb-2">
                  <Paperclip className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                  Attachments ({previews.length})
                </label>
                <div
                  ref={attachmentDropzoneRef}
                  tabIndex={0}
                  role="button"
                  aria-label="Add attachments"
                  onKeyDown={handleUploadAreaKeyDown}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleUploadAreaClick}
                  className={`border-2 border-dashed rounded-lg p-3.5 md:p-4 transition-all duration-200 cursor-pointer flex flex-col items-center justify-center space-y-1.5 min-h-[92px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 ${
                    isDragging
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                      : "border-gray-300 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500 bg-gray-50 dark:bg-slate-900/50"
                  }`}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    multiple
                    accept=".jpg,.jpeg,.png,.webp,.xlsx,.xls,.csv,.docx,.pdf,.pptx"
                    className="hidden"
                  />
                  <div className="p-2 bg-white dark:bg-slate-800 rounded-full shadow-sm">
                    <Upload className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="text-center">
                    <p className="text-xs md:text-sm font-medium text-gray-700 dark:text-slate-200">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-0.5">
                      JPG, PNG, WEBP, XLSX, XLS, CSV, DOCX, PDF, PPTX
                    </p>
                  </div>
                </div>
                {previews.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-4">
                    {previews.map((preview, index) => (
                      <div
                        key={preview.id}
                        className="group relative aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-800 animate-slideDownFade"
                      >
                        {preview.isImage ? (
                          <img
                            src={preview.url}
                            alt={`Preview ${index}`}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 px-3 text-center">
                            <div className="text-xs font-semibold px-3 py-1 rounded-full bg-white/80 dark:bg-slate-900/60 text-slate-700 dark:text-slate-200">
                              {preview.extension}
                            </div>
                            <p className="mt-3 text-xs font-medium text-slate-700 dark:text-slate-200 line-clamp-3 break-all">
                              {preview.name}
                            </p>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center space-x-2">
                          {preview.isImage && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setViewingImage(preview);
                              }}
                              className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-full transform scale-75 group-hover:scale-100 transition-all duration-200 shadow-lg"
                              title="View image"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(index);
                            }}
                            className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-full transform scale-75 group-hover:scale-100 transition-all duration-200 shadow-lg"
                            title="Remove file"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                          <p className="text-[10px] text-white truncate px-1">{preview.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Fixed Footer */}
            <div className="shrink-0 flex items-center justify-end gap-3 px-6 py-3 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700">
              <button
                type="button"
                onClick={onClose}
                className="h-8 px-4 text-xs font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg border border-gray-300 dark:border-slate-600 transition-colors flex items-center justify-center"
              >
                Cancel
              </button>

              <button
                type="submit"
                className="h-8 px-4 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Creating...</span>
                  </>
                ) : (
                  <span>Create Card</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      <ConfirmationModal isOpen={deleteConfirm.show} onClose={() => setDeleteConfirm({ show: false, index: null })} onConfirm={handleConfirmDelete} title="Remove Image" message="Are you sure you want to remove this image? This action cannot be undone before creating the card." confirmText="Remove" cancelText="Keep" type="danger" />
      {viewingImage && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 animate-in fade-in duration-200" onClick={() => setViewingImage(null)}>
          <button className="absolute top-6 right-6 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-[110]" onClick={() => setViewingImage(null)}><X className="w-6 h-6" /></button>
          <div className="relative max-w-4xl max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={viewingImage.url} alt={viewingImage.name} className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl animate-in zoom-in-95 duration-300" />
            <div className="mt-4 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white text-sm font-medium">{viewingImage.name}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateCardModal;

